/**
 * Session detail tab (details).
 */
import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import type { Session, SessionTab } from '@/types/remote/session.types';
import {
  PAIR_ONE_OFF_BADGE_CONFIG,
  SESSION_RECURRENCE_CONFIG,
  PROGRAM_BADGE_CONFIG,
} from '@/components/sessions/session-configs';
import { LabelBadge, type BadgeVariant } from '@/components/common/LabelBadge';
import {
  getSessionFacilitators,
  calcDurationMinutes,
  type SessionFacilitatorParticipant,
} from '@/components/sessions/remote/session-table-utils';
import { isPairOneOffSession } from '@/components/sessions/remote/session-form-utils';
import { Button } from '@/components/forms/Button';
import { Video, MapPin, DollarSign } from 'lucide-react';

/** Props for {@link SessionDetailsTab}. */
interface SessionDetailsTabProps {
  session: Session;
  activeTab?: SessionTab;
  typeConfig: { label: string; icon: LucideIcon; badgeColor: string } | null;
  statusConfig: {
    label: string;
    icon: LucideIcon;
    badgeVariant: BadgeVariant;
    badgeColor: string;
    isInProgress?: boolean;
  };
  sessionDate: Date;
  isTutoringSession: boolean;
  isFieldEventSession: boolean;
  isMeetingSession: boolean;
  configAnimators: SessionFacilitatorParticipant[];
  hasConfigContent: boolean;
  showJoinButton: boolean;
  isJoining: boolean;
  isInProgress: boolean;
  handleJoinMeeting: (e: React.MouseEvent) => void;
  roleConfig: Record<string, { icon?: LucideIcon; colorClass?: string } | undefined>;
}

/** Session detail tab (details). */
export const SessionDetailsTab: React.FC<SessionDetailsTabProps> = ({
  session,
  activeTab,
  typeConfig,
  statusConfig,
  sessionDate,
  isTutoringSession,
  isFieldEventSession,
  isMeetingSession,
  configAnimators,
  hasConfigContent,
  showJoinButton,
  isJoining,
  isInProgress,
  handleJoinMeeting,
  roleConfig,
}) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-6">
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Informations générales</h4>
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-gray-600">Type</dt>
            <dd className="font-medium">
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  {typeConfig && (
                    <>
                      {React.createElement(typeConfig.icon, {
                        className: 'w-4 h-4',
                        style: { color: typeConfig.badgeColor },
                      })}
                      <span>{typeConfig.label}</span>
                    </>
                  )}
                </div>
                {isPairOneOffSession(session) && (
                  <LabelBadge
                    label={PAIR_ONE_OFF_BADGE_CONFIG.label}
                    icon={PAIR_ONE_OFF_BADGE_CONFIG.icon}
                    variant={PAIR_ONE_OFF_BADGE_CONFIG.badgeVariant}
                    color={PAIR_ONE_OFF_BADGE_CONFIG.badgeColor}
                    size="sm"
                  />
                )}
              </div>
            </dd>
          </div>
          <div>
            <dt className="text-gray-600">Statut</dt>
            <dd className="mt-0.5">
              <LabelBadge
                label={statusConfig.label}
                icon={
                  statusConfig.isInProgress ? (
                    <span className="session-dot-pulse" />
                  ) : (
                    statusConfig.icon
                  )
                }
                variant={statusConfig.badgeVariant}
                color={statusConfig.badgeColor}
                size="sm"
              />
            </dd>
          </div>
          <div>
            <dt className="text-gray-600">Date</dt>
            <dd className="font-medium">
              {sessionDate.toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </dd>
          </div>
          <div>
            <dt className="text-gray-600">Horaires</dt>
            <dd className="font-medium">
              {session.startTime} - {session.endTime ?? 'N/A'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-600">Durée prévue</dt>
            <dd className="font-medium">
              {(session.durationMinutes ??
                calcDurationMinutes(session.startTime, session.endTime)) != null
                ? `${session.durationMinutes ?? calcDurationMinutes(session.startTime, session.endTime)} minutes`
                : 'N/A'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-600">Format</dt>
            <dd className="font-medium">
              {(() => {
                if (session.sessionFormat === 'in_person') return 'Présentiel';
                if (session.sessionFormat === 'hybrid') return 'Hybride';
                return 'En ligne';
              })()}
            </dd>
          </div>
          {session.recurrenceType &&
            session.recurrenceType !== 'none' &&
            session.recurrenceType !== 'once' &&
            (() => {
              const recCfg = SESSION_RECURRENCE_CONFIG[session.recurrenceType!];
              return (
                <div>
                  <dt className="text-gray-600">Récurrence</dt>
                  <dd className="mt-0.5">
                    {recCfg ? (
                      <LabelBadge
                        label={recCfg.label}
                        icon={recCfg.icon}
                        variant={recCfg.badgeVariant}
                        color={recCfg.badgeColor}
                        size="sm"
                      />
                    ) : (
                      <span className="font-medium">{session.recurrenceType}</span>
                    )}
                  </dd>
                </div>
              );
            })()}
          {session.planDuringHolidays && (
            <div>
              <dt className="text-gray-600">Vacances scolaires</dt>
              <dd className="font-medium text-green-600">Disponible</dd>
            </div>
          )}
        </dl>
      </div>

      <div>
        {isFieldEventSession &&
          (() => {
            const animators = getSessionFacilitators(session);
            if (animators.length === 0) return null;
            return (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Animateur(s)</h4>
                <div className="flex flex-wrap gap-2">
                  {animators.map((a) => {
                    const name =
                      [a.user?.firstName, a.user?.lastName].filter(Boolean).join(' ') ||
                      a.user?.email ||
                      '—';
                    const cfg = roleConfig[a.role.toUpperCase()];
                    return (
                      <LabelBadge
                        key={a.id}
                        label={name}
                        icon={cfg?.icon}
                        variant="ghost"
                        color={cfg?.colorClass ?? 'var(--color-1-nuance-2)'}
                        size="sm"
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}
        {hasConfigContent && (
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Configuration</h4>
        )}
        <dl className="space-y-2 text-sm">
          {session.establishment && !isMeetingSession && (
            <div>
              <dt className="text-gray-600">Établissement</dt>
              <dd className="font-medium">{session.establishment.name}</dd>
            </div>
          )}
          {isTutoringSession && session.managedBy && (
            <div>
              <dt className="text-gray-600">Géré par</dt>
              <dd className="font-medium">
                {session.managedBy.fullName ||
                  [session.managedBy.firstName, session.managedBy.lastName]
                    .filter(Boolean)
                    .join(' ') ||
                  '—'}
              </dd>
            </div>
          )}
          {configAnimators.length > 0 &&
            (() => {
              return (
                <div>
                  <dt className="text-gray-600 mb-1">Animateur(s)</dt>
                  <dd className="flex flex-wrap gap-1">
                    {configAnimators.map((a) => {
                      const name =
                        [a.user?.firstName, a.user?.lastName].filter(Boolean).join(' ') ||
                        a.user?.email ||
                        '—';
                      const cfg = roleConfig[a.role.toUpperCase()];
                      return (
                        <LabelBadge
                          key={a.id}
                          label={name}
                          icon={cfg?.icon}
                          variant="ghost"
                          color={cfg?.colorClass ?? 'var(--color-1-nuance-2)'}
                          size="sm"
                        />
                      );
                    })}
                  </dd>
                </div>
              );
            })()}
          {(session.programName || session.programId) && (
            <div>
              <dt className="text-gray-600 mb-1">Programme</dt>
              <dd>
                <LabelBadge
                  label={session.programName ?? session.programId ?? ''}
                  icon={PROGRAM_BADGE_CONFIG.icon}
                  variant={PROGRAM_BADGE_CONFIG.badgeVariant}
                  color={PROGRAM_BADGE_CONFIG.badgeColor}
                  size="sm"
                />
              </dd>
            </div>
          )}
          {session.createdBy && (
            <div>
              <dt className="text-gray-600">Créé par</dt>
              <dd className="font-medium">
                {session.createdBy.firstName} {session.createdBy.lastName}
              </dd>
            </div>
          )}
          {showJoinButton && (
            <div>
              <dt className="text-gray-600">Lien visio</dt>
              <dd className="mt-1">
                <Button
                  variant="outline"
                  color="rgb(var(--color-error))"
                  size="icon"
                  loading={isJoining}
                  onClick={handleJoinMeeting}
                  icon={Video}
                  className={isInProgress ? 'session-video-blink' : ''}
                  tooltipText={
                    session.type === 'welcome_meeting'
                      ? "Rejoindre réunion d'accueil"
                      : "Rejoindre réunion d'information"
                  }
                />
              </dd>
            </div>
          )}
        </dl>

        {activeTab === 'field-event' && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Événement terrain</h4>
            <dl className="space-y-2 text-sm">
              {session.eventType && (
                <div>
                  <dt className="text-gray-600">Type d'événement</dt>
                  <dd className="font-medium">{session.eventType}</dd>
                </div>
              )}
              {session.location && (
                <div>
                  <dt className="text-gray-600">Lieu</dt>
                  <dd className="font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {session.location}
                  </dd>
                </div>
              )}
              {session.budget && (
                <>
                  {session.budget.posts && session.budget.posts.length > 0 && (
                    <div>
                      <dt className="text-gray-600 mb-1">Postes budgétaires</dt>
                      <dd className="space-y-1">
                        {session.budget.posts.map((post) => (
                          <div
                            key={`${post.type}-${post.amount}`}
                            className="text-xs flex justify-between bg-gray-50 px-2 py-1 rounded"
                          >
                            <span>{post.type}</span>
                            <span className="font-medium">{post.amount} €</span>
                          </div>
                        ))}
                      </dd>
                    </div>
                  )}
                  {session.budget.etp !== undefined && (
                    <div>
                      <dt className="text-gray-600">Coût animateurs (ETP)</dt>
                      <dd className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span>Nombre ETP:</span>
                          <span className="font-medium">{session.budget.etp}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Coût unitaire:</span>
                          <span className="font-medium">{session.budget.unitCost ?? 0} €</span>
                        </div>
                        <div className="flex justify-between font-medium bg-gray-50 px-2 py-1 rounded">
                          <span>Total ETP:</span>
                          <span>{session.budget.etp * (session.budget.unitCost ?? 0)} €</span>
                        </div>
                      </dd>
                    </div>
                  )}
                  <div className="pt-2 border-t">
                    <dt className="text-gray-600">Budget total</dt>
                    <dd className="font-semibold text-lg flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {session.budget.grandTotal ?? 0} €
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>

    {session.internalNotes && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes internes</h4>
        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{session.internalNotes}</p>
      </div>
    )}
  </div>
);
